// =============================================================================
// Forgot Password Page (password auth v1, 2026-09)
// =============================================================================
// Always shows the same confirmation regardless of whether the account
// exists (anti-enumeration — mirrors the backend's always-200 contract).
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailCheck, Send } from 'lucide-react';
import { useForgotPassword } from '@/hooks/useAuth';
import { PublicNavbar } from '@/components/PublicNavbar';
import { PageBackground } from '@/components/ui/PageBackground';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const forgot = useForgotPassword();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }
    forgot.mutate(email, {
      onSuccess: () => setSubmitted(true),
      onError: () => setLocalError('Something went wrong. Please try again.'),
    });
  };

  return (
    <PageBackground>
      <div className="min-h-screen">
        <PublicNavbar />
        <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
              Reset your password
            </h2>
            {!submitted && (
              <p className="mt-2 text-center text-sm text-gray-600">
                Enter your account email and we&apos;ll send you a reset link.
              </p>
            )}
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {submitted ? (
                <div className="text-center">
                  <MailCheck className="mx-auto h-10 w-10 text-[#17DDAB]" />
                  <p className="mt-4 text-sm text-gray-700">
                    If an account exists for <span className="font-medium">{email}</span>, we&apos;ve
                    sent a password reset link. It expires in 30 minutes.
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Don&apos;t see it? Check your spam folder.
                  </p>
                  <Link
                    to="/login"
                    className="mt-6 inline-flex items-center gap-1 text-sm text-[#009FE4] hover:underline"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to sign in
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className={`mt-1 block w-full px-3 py-2 border ${
                        localError ? 'border-red-500' : 'border-gray-300'
                      } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4]`}
                    />
                    {localError && <p className="mt-2 text-sm text-red-600">{localError}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={forgot.isPending}
                    className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#17DDAB] to-[#009FE4] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009FE4] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {forgot.isPending ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send reset link
                      </>
                    )}
                  </button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to sign in
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageBackground>
  );
}
